/**
 * ExploreAfterExpiredSection
 * Shown to signed-out users on expired activity pages.
 * Displays up to 3 upcoming activity recommendations in a horizontal carousel.
 */

import { useEffect, useLayoutEffect, useRef, useState, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { ChevronLeft, ChevronRight, ArrowRight } from 'lucide-react';
import { getExploreExpiredRecommendations } from '../api/api';
import { MiniActivityCard } from '../pages/HomePage';
import { track } from '../utils/analytics';

export default function ExploreAfterExpiredSection({ activitySlug, sectionRef, onRecsLoaded }) {
  const [recs, setRecs]           = useState([]);
  const [loading, setLoading]     = useState(true);
  const [activeSlide, setActiveSlide] = useState(0);
  const [canScrollRight, setCanScrollRight] = useState(false);
  const scrollRef   = useRef(null);
  const navigate    = useNavigate();
  const trackedView = useRef(false);

  const fetchRecs = useCallback(async () => {
    if (!activitySlug) return;
    try {
      const { data } = await getExploreExpiredRecommendations(activitySlug);
      const activities = data?.activities || [];
      setRecs(activities);
      onRecsLoaded?.(activities.length);
      if (activities.length > 0 && !trackedView.current) {
        trackedView.current = true;
        track('explore_expired_page_view', { activity_id: activitySlug, num_cards: activities.length });
      }
    } catch {
      setRecs([]);
      onRecsLoaded?.(0);
    } finally {
      setLoading(false);
    }
  }, [activitySlug, onRecsLoaded]);

  useEffect(() => { fetchRecs(); }, [fetchRecs]);

  // Detect whether right-scroll is available after cards render
  useLayoutEffect(() => {
    if (!scrollRef.current || recs.length <= 1) return;
    const { scrollWidth, offsetWidth } = scrollRef.current;
    setCanScrollRight(scrollWidth > offsetWidth + 5);
  }, [recs]);

  const handleScroll = () => {
    if (!scrollRef.current || recs.length === 0) return;
    const { scrollLeft, scrollWidth, offsetWidth } = scrollRef.current;
    const maxScroll = scrollWidth - offsetWidth;
    if (maxScroll > 0) {
      const slide = Math.min(recs.length - 1, Math.round((scrollLeft / maxScroll) * (recs.length - 1)));
      setActiveSlide(slide);
      setCanScrollRight(scrollLeft < maxScroll - 5);
    }
  };

  const scrollToSlide = (idx) => {
    if (!scrollRef.current || recs.length === 0) return;
    const itemWidth = scrollRef.current.scrollWidth / recs.length;
    scrollRef.current.scrollTo({ left: idx * itemWidth, behavior: 'smooth' });
  };

  const handleCardClick = (activity, idx) => {
    track('explore_expired_card_click', {
      activity_id:      activitySlug,
      to_activity_id:   activity.id,
      to_activity_slug: activity.slug,
      position:         idx,
      criteria_level:   activity.criteria_level,
    });
    sessionStorage.setItem('karmana_explore_ref', JSON.stringify({
      from_slug:      activitySlug,
      to_activity_id: activity.id,
      criteria_level: activity.criteria_level,
    }));
    navigate(`/activities/${activity.slug || activity.id}`);
  };

  if (loading || recs.length === 0) return null;

  return (
    <div
      ref={sectionRef}
      className="bg-white border border-gray-200 rounded-xl overflow-hidden"
      data-testid="explore-after-expired-section"
    >
      {/* Heading */}
      <h2
        className="text-base font-bold text-gray-900 leading-snug px-4 pt-4 pb-3 flex items-center gap-1"
        style={{ background: '#fefce8' }}
      >
        Similar Activities Others are Interested In
        <ArrowRight size={13} className="text-gray-700 flex-shrink-0 ml-1.5" />
      </h2>

      {/* Carousel */}
      <div className="relative">
        {/* Left arrow — desktop only */}
        {activeSlide > 0 && (
          <button
            onClick={() => scrollToSlide(activeSlide - 1)}
            className="hidden md:flex absolute left-2 top-1/2 -translate-y-1/2 z-10 w-8 h-8 bg-white/95 border border-gray-200 rounded-full shadow-sm items-center justify-center hover:bg-gray-50 transition-colors"
            aria-label="Previous slide"
            data-testid="explore-carousel-prev"
          >
            <ChevronLeft size={16} className="text-gray-600" />
          </button>
        )}
        {/* Right arrow — desktop only */}
        {canScrollRight && (
          <button
            onClick={() => scrollToSlide(activeSlide + 1)}
            className="hidden md:flex absolute right-2 top-1/2 -translate-y-1/2 z-10 w-8 h-8 bg-white/95 border border-gray-200 rounded-full shadow-sm items-center justify-center hover:bg-gray-50 transition-colors"
            aria-label="Next slide"
            data-testid="explore-carousel-next"
          >
            <ChevronRight size={16} className="text-gray-600" />
          </button>
        )}

        {/* Scrollable cards */}
        <div
          ref={scrollRef}
          className="flex gap-3 overflow-x-auto snap-x snap-mandatory px-4"
          style={{ scrollbarWidth: 'none', msOverflowStyle: 'none', WebkitOverflowScrolling: 'touch' }}
          onScroll={handleScroll}
        >
          {recs.map((activity, idx) => (
            <div
              key={activity.id}
              className="flex-shrink-0 snap-start pb-2"
              style={{ width: 'clamp(200px, 66vw, 376px)' }}
              data-testid={`explore-rec-card-${idx}`}
            >
              <MiniActivityCard
                activity={activity}
                organizerName={activity.organizer_name || activity.other_organizer_name}
                organizerLogo={activity.organizer_logo}
                index={idx}
                onClick={() => handleCardClick(activity, idx)}
              />
            </div>
          ))}
        </div>

        {/* Dot indicators — both mobile and desktop */}
        {recs.length > 1 && (
          <div className="flex justify-center gap-1.5 pt-3 pb-1" data-testid="explore-carousel-dots">
            {recs.map((_, i) => (
              <button
                key={i}
                onClick={() => scrollToSlide(i)}
                aria-label={`Go to slide ${i + 1}`}
                className={`rounded-full transition-all ${i === activeSlide ? 'w-4 h-1.5 bg-gray-700' : 'w-1.5 h-1.5 bg-gray-300'}`}
              />
            ))}
          </div>
        )}
      </div>

      <div className="pb-4" />
    </div>
  );
}
